import React, { useState } from "react";
import ReactApexChart from "react-apexcharts";
import { resolveImg } from "../../Api/Api";
import { RANGE_PRESETS, fmt, compact } from "./format";

/// Date-range picker: presets + custom from/to. Calls onChange({from,to}).
export function RangeBar({ value, onChange }) {
  const [preset, setPreset] = useState(value?.preset || "all");

  const pick = (p) => {
    setPreset(p.key);
    onChange({ ...p.range(), preset: p.key });
  };

  return (
    <div className="rv-range">
      {RANGE_PRESETS.map((p) => (
        <button
          key={p.key}
          type="button"
          className={`rv-chip ${preset === p.key ? "active" : ""}`}
          onClick={() => pick(p)}
        >
          {p.label}
        </button>
      ))}
      <input
        type="date"
        className="rv-date"
        value={value?.from || ""}
        max={value?.to || undefined}
        onChange={(e) => {
          setPreset("custom");
          onChange({ from: e.target.value, to: value?.to || e.target.value, preset: "custom" });
        }}
      />
      <span className="text-grey">→</span>
      <input
        type="date"
        className="rv-date"
        value={value?.to || ""}
        min={value?.from || undefined}
        onChange={(e) => {
          setPreset("custom");
          onChange({ from: value?.from || e.target.value, to: e.target.value, preset: "custom" });
        }}
      />
    </div>
  );
}

export function Kpi({ label, value, foot, tone = "", title }) {
  return (
    <div className={`rv-kpi ${tone}`} title={title}>
      <div className="rv-kpi-label">{label}</div>
      <div className="rv-kpi-value">{value}</div>
      {foot && <div className="rv-kpi-foot">{foot}</div>}
    </div>
  );
}

export function Avatar({ img, name, size = "" }) {
  const [broken, setBroken] = useState(false);
  if (img && !broken) {
    return <img className={`rv-avatar ${size}`} src={resolveImg(img)} alt={name || ""} onError={() => setBroken(true)} />;
  }
  return <span className={`rv-avatar ${size}`}>{(name || "?").charAt(0).toUpperCase()}</span>;
}

export function UserCell({ user, sub }) {
  if (!user) return <span className="text-grey">—</span>;
  return (
    <div className="rv-user">
      <Avatar img={user.img} name={user.name} />
      <div style={{ minWidth: 0 }}>
        <div className="rv-user-name">{user.name ? (user.type === "category" ? user.name : `@${user.name}`) : `#${user.id}`}</div>
        <div className="rv-user-sub">{sub ?? user.nick_name ?? ""}</div>
      </div>
    </div>
  );
}

export function Progress({ value, tone = "", size = "" }) {
  const v = Math.max(0, Math.min(100, Number(value || 0)));
  return (
    <div className={`rv-progress ${tone} ${size}`}>
      <span style={{ width: `${v}%` }} />
    </div>
  );
}

/// Simple prev/next pager for the backend's {total, per_page, current_page, last_page} meta.
export function Pager({ meta, onPage }) {
  if (!meta || meta.last_page <= 1) return null;
  const from = (meta.current_page - 1) * meta.per_page + 1;
  const to = Math.min(meta.total, meta.current_page * meta.per_page);
  return (
    <div className="rv-pager">
      <span>
        {fmt(from)}–{fmt(to)} of {fmt(meta.total)}
      </span>
      <div className="d-flex gap-2">
        <button className="rv-btn ghost" disabled={meta.current_page <= 1} onClick={() => onPage(meta.current_page - 1)}>
          Prev
        </button>
        <span className="align-self-center">
          {meta.current_page} / {meta.last_page}
        </span>
        <button
          className="rv-btn ghost"
          disabled={meta.current_page >= meta.last_page}
          onClick={() => onPage(meta.current_page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}

// Series colours — all from the admin's own palette.
export const SERIES = [
  { key: "host", name: "Hosts (diamonds)", color: "#fc155c" },
  { key: "app", name: "App", color: "#7437d0" },
  { key: "category", name: "Categories", color: "#01b574" },
  { key: "store", name: "Store (30%)", color: "#e4ba56" },
  { key: "pending", name: "Pending", color: "#a96fff" },
  // Market (≈ USD)
  { key: "sellers_share", name: "Sellers' share", color: "#fc155c" },
  { key: "app_commission", name: "App commission", color: "#7437d0" },
];

/// Stacked bars per day/month. `keys` picks which SERIES to draw.
export function RevenueChart({ points = [], keys = SERIES.map((s) => s.key), height = 320, emptyText = "No gifts in this range yet." }) {
  const series = SERIES.filter((s) => keys.includes(s.key));
  const options = {
    chart: { type: "bar", stacked: true, toolbar: { show: false }, background: "transparent", foreColor: "#a0aec0" },
    theme: { mode: "dark" },
    colors: series.map((s) => s.color),
    plotOptions: { bar: { columnWidth: points.length > 40 ? "80%" : "55%", borderRadius: 3 } },
    dataLabels: { enabled: false },
    xaxis: {
      categories: points.map((p) => p.label),
      labels: { style: { colors: "#a0aec0" }, rotate: -45, hideOverlappingLabels: true },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: { style: { colors: "#a0aec0" }, formatter: (v) => compact(v) } },
    grid: { borderColor: "rgba(255,255,255,0.06)", strokeDashArray: 4 },
    legend: { labels: { colors: "#ffffff" }, position: "top", horizontalAlign: "left" },
    tooltip: { theme: "dark", y: { formatter: (v) => fmt(v) } },
  };
  const data = series.map((s) => ({ name: s.name, data: points.map((p) => Number(p[s.key] || 0)) }));

  if (!points.length) {
    return <div className="rv-empty">{emptyText}</div>;
  }
  return <ReactApexChart options={options} series={data} type="bar" height={height} />;
}

/// Horizontal share bar (store / host / app / category / pending) with legend.
export function SplitBar({ parts }) {
  const total = parts.reduce((a, p) => a + Number(p.value || 0), 0);
  return (
    <div>
      <div className="rv-split">
        {parts.map((p) =>
          p.value > 0 ? (
            <span key={p.label} style={{ width: `${(p.value / total) * 100}%`, background: p.color }} title={`${p.label}: ${fmt(p.value)}`} />
          ) : null
        )}
      </div>
      <div className="rv-legend">
        {parts.map((p) => (
          <span key={p.label}>
            <span className="rv-dot" style={{ background: p.color }} />
            {p.label}: <b className="text-white">{fmt(p.value)}</b>
            {total > 0 && <span> ({Math.round((p.value / total) * 1000) / 10}%)</span>}
          </span>
        ))}
      </div>
    </div>
  );
}

export const Loading = () => (
  <div className="rv-empty">
    <div className="spinner-border spinner-border-sm me-2" role="status" /> Loading…
  </div>
);

export default React.memo(RevenueChart);
